import React, { forwardRef, useImperativeHandle, useRef } from 'react';
import { Feedback, Input, Field } from '@availity/form';
import * as yup from 'yup';
import {
    defaultTimelineAction,
    TimelineAction,
} from '../../../../slices/marketplace/timeline/models/timeline-actions.model';
import { TimelineEvent } from '../../../../slices/marketplace/timeline/models/timeline-event.model';
import {
    BuyerExpressionOfInterestOptionsList,
    TimelineActionType,
} from '../../../../slices/marketplace/timeline/models/timeline-action-type.model';
import { Formik } from 'formik';
import { LevelOfService } from '../../../../slices/marketplace/timeline/models/enums/level-of-service.enum';
import { BuyerExpressionOfInterestSettings } from '../../../../slices/marketplace/timeline/models/buyer-expression-of-interest-settings.model';

interface BuyerExpressionOfInterestFormProps {
    onSubmit(
        action: TimelineAction,
        expressionOfInterestSettings?: BuyerExpressionOfInterestSettings
    ): void;
    disabled: boolean;
    timelineEvents: TimelineEvent[];
    currentEvent: TimelineEvent;
    linkedAction?: TimelineAction;
    levelOfService: LevelOfService;
    expressionOfInterestSettings?: BuyerExpressionOfInterestSettings;
}

export const BuyerExpressionOfInterestForm = forwardRef(
    (props: BuyerExpressionOfInterestFormProps, ref) => {
        const formRef = useRef<any>();

        useImperativeHandle(ref, () => ({
            handleSubmit() {
                formRef?.current?.handleSubmit();
            },
        }));

        return (
            <Formik
                innerRef={formRef}
                initialValues={{
                    actionType: BuyerExpressionOfInterestOptionsList.some(
                        (s) => s.value === props.linkedAction?.actionType
                    )
                        ? props.linkedAction?.actionType
                        : TimelineActionType.BuyerExpressionOfInterest,
                    daysAfterTeaserPosted:
                        props.expressionOfInterestSettings
                            ?.daysAfterTeaserWasPosted ?? 0,
                    daysBeforeCriticalActionDeadline:
                        props.expressionOfInterestSettings
                            ?.daysBeforeCriticalActionDeadline ?? 0,
                }}
                validationSchema={yup.object().shape({
                    actionType: yup
                        .number()
                        .uniqueActionValidation(
                            props.timelineEvents ?? [],
                            props.currentEvent
                        ),
                    daysAfterTeaserPosted: yup.number().when('actionType', {
                        is: (val: TimelineActionType) =>
                            props.levelOfService ===
                                LevelOfService.StrategicFinancialAdvisoryService &&
                            val == TimelineActionType.BuyerExpressionOfInterest,
                        then: () =>
                            yup
                                .number()
                                .required()
                                .min(1, 'Offset must be at least 1 day'),
                    }),
                    daysBeforeCriticalActionDeadline: yup
                        .number()
                        .when('actionType', {
                            is: (val: TimelineActionType) =>
                                props.levelOfService ===
                                    LevelOfService.StrategicFinancialAdvisoryService &&
                                val ==
                                    TimelineActionType.BuyerExpressionOfInterest,
                            then: () =>
                                yup
                                    .number()
                                    .required()
                                    .min(1, 'Offset must be at least 1 day'),
                        }),
                })}
                onSubmit={(values) => {
                    props.onSubmit(
                        {
                            ...defaultTimelineAction,
                            actionType: Number(values.actionType),
                        },
                        {
                            id: props.expressionOfInterestSettings?.id ?? 0,
                            daysAfterTeaserWasPosted:
                                values.daysAfterTeaserPosted,
                            daysBeforeCriticalActionDeadline:
                                values.daysBeforeCriticalActionDeadline,
                        }
                    );
                }}>
                {({ values, setFieldValue }) => {
                    return (
                        <>
                            <Input
                                type="select"
                                name="actionType"
                                disabled={props.disabled}
                                onChange={(event) => {
                                    setFieldValue(
                                        'actionType',
                                        Number(event.target.value)
                                    );
                                }}>
                                {BuyerExpressionOfInterestOptionsList.map(
                                    (option) => (
                                        <option
                                            key={option.value}
                                            value={option.value}>
                                            {option.label}
                                        </option>
                                    )
                                )}
                            </Input>
                            <Feedback name="actionType" />

                            {values.actionType ===
                                TimelineActionType.BuyerExpressionOfInterest &&
                                props.levelOfService ===
                                    LevelOfService.StrategicFinancialAdvisoryService && (
                                    <>
                                        <Field
                                            className="field"
                                            labelClass={'mt-3'}
                                            name="daysAfterTeaserPosted"
                                            label={`Days after Teaser was posted to Buyer`}
                                            disabled={props.disabled}
                                        />

                                        <Field
                                            className="field"
                                            name="daysBeforeCriticalActionDeadline"
                                            label={`Days before Id NDA Deadline`}
                                            disabled={props.disabled}
                                        />
                                    </>
                                )}
                        </>
                    );
                }}
            </Formik>
        );
    }
);
